import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Sun, History, Bell, Users, Settings as SettingsIcon,
  MapPin, Link2, LogOut, Menu, X, SlidersHorizontal, Cpu, HeartPulse, TrendingUp, Leaf
} from 'lucide-react';
import { useAuth } from './auth';
import './AppShell.css';

// Not admin-gated - a normal assigned user needs their own plants'
// production forecast/environmental impact exactly as much as an admin
// does. Sits alongside Plants/History/Alerts, not under Administration.
const NAV_ITEMS = [
  { to: '/', label: 'Tableau de bord', icon: LayoutDashboard, end: true },
  { to: '/plants', label: 'Centrales', icon: Sun },
  { to: '/production-forecast', label: 'Prévision de Production', icon: TrendingUp },
  { to: '/history', label: 'Historique', icon: History },
  { to: '/alerts', label: 'Alertes', icon: Bell },
  { to: '/environmental-impact', label: 'Impact Environnemental', icon: Leaf },
  { to: '/settings', label: 'Paramètres', icon: SettingsIcon }
];

const ADMIN_NAV_ITEMS = [
  { to: '/admin/users', label: 'Utilisateurs', icon: Users },
  { to: '/admin/plants', label: 'Gestion des Centrales', icon: MapPin },
  { to: '/admin/assignments', label: 'Affectations', icon: Link2 },
  { to: '/admin/alert-rules', label: 'Configuration des Alertes', icon: SlidersHorizontal },
  { to: '/admin/devices', label: 'Appareils', icon: Cpu },
  { to: '/admin/system-health', label: 'État du Système', icon: HeartPulse }
];

export default function AppShell({ realTimeData, weatherData, plants, stats, socket }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isAdmin = user?.role === 'admin';

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const renderNavLink = ({ to, label, icon: Icon, end }) => (
    <NavLink
      key={to}
      to={to}
      end={end}
      className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}
      onClick={() => setSidebarOpen(false)}
    >
      <Icon size={18} />
      <span>{label}</span>
    </NavLink>
  );

  return (
    <div className="app-shell">
      <button className="mobile-menu-toggle" onClick={() => setSidebarOpen((v) => !v)} aria-label="Menu">
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      <aside className={`sidebar${sidebarOpen ? ' open' : ''}`}>
        <div className="sidebar-brand">
          <span className="sidebar-brand-icon">🌞</span>
          <span className="sidebar-brand-text">Solar Monitor</span>
        </div>
        <nav className="sidebar-nav">
          {NAV_ITEMS.map(renderNavLink)}
          {isAdmin && (
            <>
              <div className="sidebar-section-label">Administration</div>
              {ADMIN_NAV_ITEMS.map(renderNavLink)}
            </>
          )}
        </nav>
      </aside>

      <div className="app-shell-main">
        <header className="topbar">
          <div className="topbar-spacer" />
          <div className="topbar-user">
            <span className={`role-badge role-${user?.role}`}>{user?.role === 'admin' ? 'Administrateur' : 'Utilisateur'}</span>
            <span className="topbar-email">{user?.email}</span>
            <button className="topbar-logout" onClick={handleLogout} title="Déconnexion">
              <LogOut size={16} />
            </button>
          </div>
        </header>

        <main className="app-content">
          <Outlet context={{ realTimeData, weatherData, plants, stats, socket }} />
        </main>
      </div>
    </div>
  );
}
